import React, { useState, type FC, type PropsWithChildren, useContext } from 'react';

import { getI18nData } from '@lucid/utils';

import type { Lucid, CustomDrupal, I18n } from '../../types/types';
import Nav, { type NavOptions, type ContextUseStateType } from '../../components/nav/nav';
import type { NavMenuOptions } from '../../components/nav/nav-menu';
import NavSkipButton from '../../components/nav/nav-skip-button';
import type { ComponentImage } from '../../types/generics';
import LayoutContext from '../../components/context/layout-context';

export interface SiteNavOptions extends NavOptions, NavMenuOptions {
  /** Renders the top-level nav if enabled. */
  hasTopNav?: boolean;
  /** The component variants. */
  variants?: {
    logo?: Lucid.Component.Nav['Logo']['Variant'];
  };
}

export interface TopNavProps {
  /** The top level Navigation buttons. */
  topButtons?: CustomDrupal.LinkInterface[];
  /** The top level Navigation items. */
  topItems?: CustomDrupal.LinkInterface[];
  /** The Logo object containing alt and src keys. */
  topLogo?: ComponentImage;
  /** The link that wraps the Logo. */
  topLogoLink?: string;
  /** The top level Navigation placeholder links for SSR. */
  placeholderLinks?: CustomDrupal.LinkInterface[];
}

export interface SiteNavProps extends TopNavProps, SiteNavOptions {
  /** The aria label for screen readers */
  buttonAriaLabel: string;
  /** The bottom level Navigation buttons. */
  buttons: CustomDrupal.LinkInterface[];
  /** The i18n translation data. */
  i18nData: I18n.DataEdges;
  /** The bottom level Navigation items. */
  items: CustomDrupal.LinkInterface[];
  /** The Logo object containing alt and src keys. */
  logo: ComponentImage;
  /** The link that wraps the Logo. */
  logoLink?: string;
  /** The bottom level Navigation placeholder buttons for SSR. */
  placeholderButtons: CustomDrupal.LinkInterface[];
  /** The text for the view more link. */
  viewMoreText?: string;
}

const SiteNav: FC<PropsWithChildren<SiteNavProps>> = ({
  buttonAriaLabel,
  buttons,
  entityTranslations = [],
  hasTopNav = false,
  isSticky = false,
  isTopNav = false,
  i18nData,
  items,
  logo,
  logoLink,
  placeholderButtons,
  placeholderLinks,
  showNavbarOnScrollDelay = 350,
  topButtons,
  topItems,
  topLogo,
  topLogoLink,
  viewMoreText,
  willAlwaysHaveBorder = false,
  willAlwaysHaveShadow = false,
  willHaveBorderOnScroll = false,
  willHaveShadowOnScroll = false,
  willShowNavbarOnScroll = false,
  variants,
  ...rest
}) => {
  const lucidSuiteLogo = {
    imageSrc: 'https://cdn-cashy-static-assets.lucidchart.com/lucid/logos/lucid.svg',
    imageAlt: 'Lucid Logo',
  };
  const [[activeMenuCallback], setActiveMenuCallback] = useState<ContextUseStateType>([]);

  const handleActiveMenu = (currentActiveMenu: ContextUseStateType) => {
    const [specificCurrentActiveMenu] = currentActiveMenu;

    if (!activeMenuCallback) {
      return setActiveMenuCallback(currentActiveMenu);
    }

    if (activeMenuCallback !== specificCurrentActiveMenu) {
      activeMenuCallback(false);
      return setActiveMenuCallback(currentActiveMenu);
    }
  };

  const context = {
    setActiveMenuCallback: handleActiveMenu,
  };

  const {
    language: { i18nKey },
  } = useContext(LayoutContext);
  const i18n = getI18nData(i18nData, i18nKey);

  return (
    <Nav.SiteContext.Provider value={context}>
      {hasTopNav && (
        <>
          <NavSkipButton />
          <Nav {...rest} isTopNav ariaLabelText={i18n('mktg-top-nav-aria-label')} viewMoreText={viewMoreText}>
            <Nav.Logo isTopNav logo={topLogo || lucidSuiteLogo} logoLink={topLogoLink || 'https://lucid.co'} />
            <Nav.Menu
              isTopNav
              buttonAriaLabel={buttonAriaLabel}
              buttons={topButtons || []}
              entityTranslations={entityTranslations}
              i18nData={i18nData}
              items={topItems || []}
              placeholderLinks={placeholderLinks || []}
            />
          </Nav>
        </>
      )}
      {!hasTopNav && <NavSkipButton />}
      <Nav
        {...rest}
        ariaLabelText={i18n('mktg-nav-aria-label')}
        isSticky={isSticky}
        showNavbarOnScrollDelay={showNavbarOnScrollDelay}
        viewMoreText={viewMoreText}
        willAlwaysHaveBorder={willAlwaysHaveBorder}
        willAlwaysHaveShadow={willAlwaysHaveShadow}
        willHaveBorderOnScroll={willHaveBorderOnScroll}
        willHaveShadowOnScroll={willHaveShadowOnScroll}
        willShowNavbarOnScroll={willShowNavbarOnScroll}
      >
        <Nav.Logo logo={logo} logoLink={logoLink} variant={variants?.logo} />
        <Nav.Menu
          buttonAriaLabel={buttonAriaLabel}
          buttons={buttons}
          i18nData={i18nData}
          isTopNav={isTopNav}
          items={items}
          placeholderButtons={placeholderButtons}
        />
      </Nav>
    </Nav.SiteContext.Provider>
  );
};

export { SiteNav };
export default SiteNav;
